// The booking rate's reads (BL-003), for both drawings: Riverstone's (BookingRate.tsx) and Blueprint's
// (BookingRateSection.tsx). The week's own line (a CSR's, or everyone's for an owner or manager), read
// again whenever a call's reason, outcome or review may have changed (P-0052); and the rate over a range,
// by who took the calls, by reason or by campaign.
import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { rangeOf, rateLine, type RangeKey, type RateRow } from './reasons-model.ts';

export type By = 'csr' | 'reason' | 'campaign';
export type Rate = { rows: RateRow[]; total: RateRow; missed: number; takersSince: string | null };

export const RANGES: [RangeKey, string][] = [
  ['week', 'This week'],
  ['month', 'This month'],
  ['last_month', 'Last month'],
];
export const BYS: [By, string][] = [
  ['csr', 'By who took them'],
  ['reason', 'By reason'],
  ['campaign', 'By campaign'],
];

/** "This week: 6 of 8 leads booked (75%)", or everyone's for a reviewer; null until it's read. */
export function useMyRate(today: string, everyone: boolean, version: string): string | null {
  const [r, setR] = useState<Rate | null>(null);
  useEffect(() => {
    void client
      .call<Rate>('calls.bookingRate', rangeOf('week', today))
      .then(setR)
      .catch(() => setR(null));
  }, [today, version]);
  if (!r) return null;
  const row = everyone ? r.total : r.rows[0];
  if (!row) return null;
  return `This week${everyone ? ', everyone' : ''}: ${rateLine(row)}`;
}

/** The rate over a range, by `by`: null while it's read; `error` in the server's words. */
export function useBookingRate(range: RangeKey, by: By, today: string) {
  const [r, setR] = useState<Rate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [again, setAgain] = useState(0);
  useEffect(() => {
    setR(null);
    setError(null);
    void client
      .call<Rate>('calls.bookingRate', { ...rangeOf(range, today), by })
      .then(setR)
      .catch((e: unknown) =>
        setError(e instanceof RpcError ? e.message : 'Could not read the booking rate.'),
      );
  }, [range, by, today, again]);
  return { r, error, retry: () => setAgain((n) => n + 1) };
}

/** A row's counts: "3 taken · 1 excused · 1 under a minute · 2 with no reason yet". */
export const countsLine = (x: RateRow) =>
  [
    `${x.taken} taken`,
    x.excused ? `${x.excused} excused` : null,
    x.underAMinute ? `${x.underAMinute} under a minute` : null,
    x.notSet ? `${x.notSet} with no reason yet` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** "2 reasons changed in review", or nothing. */
export const changedWord = (x: RateRow) =>
  x.changedInReview > 0
    ? `${x.changedInReview} ${x.changedInReview === 1 ? 'reason' : 'reasons'} changed in review`
    : null;

/** "3 missed calls: nobody took them.", or nothing. */
export const missedLine = (missed: number) =>
  missed > 0
    ? `${missed} missed ${missed === 1 ? 'call' : 'calls'}: nobody took ${missed === 1 ? 'it' : 'them'}.`
    : null;
